import {
  signIn,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  updatePassword,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

import { AuthSourceEnum } from '../../../accounts/constants/auth-source.ts';

import {
  AwsAuthUtilSignInOptions,
  AwsCurrentUserType,
  TokenResolverFunction,
  UserRole,
} from '../../types/aws.types.ts';

import { getAppBuildUtil } from '../app-build.util.ts';
import { getLocaleUtil } from '../locale.util.ts';
import { AwsCookieStorageUtil } from '../storage/aws-cookie-storage.util.ts';
import { getToasterUtil } from '../toaster.util.ts';

export class AwsAuthUtil {
  private tokenResolvers: TokenResolverFunction[] = [];
  private isRefreshingToken = false;
  private storage: AwsCookieStorageUtil | undefined;

  private readonly AWS_TOKEN_EXPIRATION_THRESHOLD = 1000 * 60 * 10; // 10 minutes

  private static _instance: AwsAuthUtil;

  static get instance(): AwsAuthUtil {
    if (!AwsAuthUtil._instance) {
      AwsAuthUtil._instance = new AwsAuthUtil();
    }

    return AwsAuthUtil._instance;
  }

  private constructor(
    private readonly appBuildUtil = getAppBuildUtil(),
    private readonly toasterUtil = getToasterUtil(),
    private readonly localeUtil = getLocaleUtil()
  ) {
    this.initialize();
  }

  initialize(): void {
    // Set default value to use cookie storage, which is the default for remember me
    // if turns out user did not select remember me, it will be removed once user close the browser
    this.setTokenStorage(true);
  }

  private setTokenStorage(isRememberMe: boolean): void {
    try {
      const storage = new AwsCookieStorageUtil({
        domain: window.location.hostname,
        secure: window.location.protocol === 'https:',
        path: '/',
        expires: isRememberMe ? 365 : undefined,
        sameSite: 'strict',
      });

      cognitoUserPoolsTokenProvider.setKeyValueStorage(storage);
      this.storage = storage;
    } catch (_e) {
      /* empty */
    }
  }

  getStorage(): AwsCookieStorageUtil | undefined {
    return this.storage;
  }

  private forceRemoveCognitoCookie(): void {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith('CognitoIdentityServiceProvider')) {
        const cookieName = trimmedCookie.split('=')[0];
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname};`;
      }
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.getIdToken();
    return Boolean(token);
  }

  async getCurrentUser(): Promise<undefined | AwsCurrentUserType> {
    const isAuthenticated = await this.isAuthenticated();
    if (!isAuthenticated) {
      return;
    }

    const currentUser = await fetchUserAttributes().catch(() => undefined);
    const userId = currentUser?.['custom:userID'];
    const preferredName = currentUser?.['custom:preferredName'];
    const studentId = currentUser?.['custom:studentID'];
    const email = currentUser?.email;

    if (!userId || !email) {
      return;
    }

    return {
      email,
      userId,
      preferredName,
      studentId,
    };
  }

  async signIn(email: string, password: string, options: AwsAuthUtilSignInOptions): Promise<void> {
    this.setTokenStorage(options.isRememberMe);

    const isProduction = this.appBuildUtil.isProductionBuild();

    await signIn({
      username: email,
      password,
      ...(!isProduction && {
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      }),
    });
  }

  async signOut(): Promise<void> {
    await signOut().catch(() => undefined);
    this.forceRemoveCognitoCookie();
  }

  async checkTokenAvailability(): Promise<boolean> {
    if (!this.storage) {
      return false;
    }

    const authSession = await fetchAuthSession().catch(() => undefined);
    return Boolean(authSession?.tokens?.accessToken);
  }

  async isTokenExpired(): Promise<boolean> {
    if (!this.storage) {
      return false;
    }

    const authSession = await fetchAuthSession().catch(() => undefined);
    const expirationDateTimeNumber = authSession?.tokens?.idToken?.payload?.exp;
    if (!expirationDateTimeNumber) {
      return true;
    }

    const tokenExpiryDate = new Date(expirationDateTimeNumber * 1000);
    const currentDate = new Date();

    const diff = tokenExpiryDate.getTime() - currentDate.getTime();
    return diff < this.AWS_TOKEN_EXPIRATION_THRESHOLD;
  }

  async getIdToken(): Promise<string | undefined> {
    const isTokenAvailable = await this.checkTokenAvailability();
    if (!isTokenAvailable) {
      return undefined;
    }

    const isTokenExpired = await this.isTokenExpired();
    if (isTokenExpired) {
      return await new Promise((resolve) => this.forceRefreshToken(resolve));
    }

    // If token is refreshing, wait for it to finish so we get the latest token
    if (this.isRefreshingToken) {
      return await new Promise((resolve) => this.forceRefreshToken(resolve));
    }

    const result = await fetchAuthSession().catch(() => undefined);
    if (!result?.tokens?.idToken?.toString()) {
      return await new Promise((resolve) => this.forceRefreshToken(resolve));
    }

    return result.tokens.idToken.toString();
  }

  async forceRefreshToken(resolver?: TokenResolverFunction): Promise<void> {
    if (resolver) {
      this.tokenResolvers.push(resolver);
    }

    if (this.isRefreshingToken) {
      return;
    }

    this.isRefreshingToken = true;

    const isTokenAvailable = await this.checkTokenAvailability();

    // If access-token and refresh-token is not available, no need to refresh the token
    if (!isTokenAvailable) {
      this.isRefreshingToken = false;
      this.tokenResolvers.forEach((resolve) => resolve(undefined));
      return;
    }

    const result = await fetchAuthSession({ forceRefresh: true }).catch(() => undefined);
    const token = result?.tokens?.idToken?.toString();

    this.tokenResolvers.forEach((resolve) => resolve(token));
    this.tokenResolvers = [];

    this.isRefreshingToken = false;

    // If token is still not available after refresh, sign out the user
    if (!token) {
      this.toasterUtil.error(this.localeUtil.select('auth:auth_token_expired'));
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await this.signOut();

      window.location.reload();
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await resetPassword({
      username: email,
      options: {
        clientMetadata: {
          source: AuthSourceEnum.READING_CRITTERS,
        },
      },
    });
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }

  async changePasword(oldPassword: string, newPassword: string): Promise<void> {
    await updatePassword({
      oldPassword,
      newPassword,
    });
  }

  async isParentOrGuardian(): Promise<boolean> {
    return this.hasOneOfTheGroups([UserRole.PARENT, UserRole.GUARDIAN]);
  }

  private async getUserGroups(): Promise<UserRole[]> {
    const authSession = await fetchAuthSession().catch(() => undefined);
    const groups = (authSession?.tokens?.idToken?.payload?.['cognito:groups'] ?? []) as UserRole[];
    return groups;
  }

  private async hasOneOfTheGroups(groups: UserRole[]): Promise<boolean> {
    const userGroups = await this.getUserGroups();
    if (userGroups.length === 0) {
      return false;
    }

    return groups.some((group) => userGroups.includes(group));
  }
}

export const getAwsAuthUtil = (): AwsAuthUtil => AwsAuthUtil.instance;
